import { ShieldCheck, ClipboardCheck, Clock, BadgeCheck, Award, Star, Quote } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { reviews } from "@/lib/site-config";
import { usePublicConfig } from "@/hooks/usePublicConfig";
import { buildLocalBusinessSchema, type ComplianceContent, type RoutingTargets, type TrustContent } from "@/lib/site-content.functions";

const ICONS = [ShieldCheck, Award, Clock, BadgeCheck];

function buildSchema(trust: TrustContent, routing: RoutingTargets, compliance: ComplianceContent) {
  const base = buildLocalBusinessSchema({
    trust,
    routing,
    compliance,
    url: "https://woodseerautos.co.uk",
    image: "https://woodseerautos.co.uk/images/hero-homepage.jpg",
  });
  return {
    ...base,
    review: reviews.slice(0, 4).map((r) => ({
      "@type": "Review",
      author: { "@type": "Person", name: r.name },
      reviewRating: { "@type": "Rating", ratingValue: String(r.rating), bestRating: "5", worstRating: "1" },
      reviewBody: r.text,
      itemReviewed: { "@type": "AutoRepair", name: compliance.tradingName },
    })),
  };
}

export function TrustSection() {
  const { trust, routing, compliance } = usePublicConfig();

  return (
    <section className="py-16 md:py-24 bg-foreground text-background relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,oklch(0.48_0.22_22/0.15),transparent_60%)]" />

      <div className="relative mx-auto max-w-7xl px-4 md:px-6">
        <div className="text-center max-w-3xl mx-auto">
          <p className="text-[11px] font-bold tracking-[0.25em] uppercase text-primary">{trust.eyebrow}</p>
          <h2 className="mt-3 text-3xl md:text-5xl font-extrabold tracking-tight">
            {trust.heading} <span className="text-primary">{trust.headingAccent}</span>
          </h2>
          <p className="mt-4 text-base text-background/60 max-w-2xl mx-auto leading-relaxed">{trust.intro}</p>
        </div>

        <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          {trust.credentials.map((c, i) => {
            const Icon = ICONS[i % ICONS.length];
            return (
              <div
                key={c.title + i}
                className="rounded-2xl border border-background/10 bg-background/5 backdrop-blur-sm p-5 md:p-6 hover:border-primary/40 hover:bg-background/[0.07] transition-all"
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/15 text-primary">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 text-base md:text-lg font-bold text-background">{c.title}</h3>
                <p className="mt-1 text-sm font-semibold text-primary">{c.detail}</p>
                <p className="mt-2 text-xs text-background/50 leading-relaxed">{c.sub}</p>
              </div>
            );
          })}
        </div>

        {trust.reviewSnippets.length > 0 && (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-3">
            {trust.reviewSnippets.slice(0, 3).map((snippet, i) => (
              <div
                key={i}
                className="rounded-xl border border-background/10 bg-background/[0.04] p-4 flex gap-3"
              >
                <Quote className="h-4 w-4 text-primary/70 shrink-0 mt-0.5" />
                <p className="text-sm text-background/80 leading-relaxed italic">"{snippet}"</p>
              </div>
            ))}
          </div>
        )}

        <div className="mt-8 rounded-2xl border border-background/10 bg-background/[0.04] p-5 md:p-6">
          <div className="flex flex-col md:flex-row items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className={`h-5 w-5 ${i < Math.round(parseFloat(trust.rating)) ? "fill-[#FBBC04] text-[#FBBC04]" : "fill-[#FBBC04]/40 text-[#FBBC04]/60"}`} />
                ))}
              </div>
              <div>
                <p className="text-base font-bold text-background">
                  <span className="text-primary">{trust.rating} / 5</span> on Google Reviews
                </p>
                <p className="text-xs text-background/50">From {trust.reviewCount}+ verified local customer reviews</p>
              </div>
            </div>
            <div className="flex items-center gap-3 text-xs text-background/60">
              <span className="hidden sm:inline-flex items-center gap-1.5"><ClipboardCheck className="h-3.5 w-3.5 text-primary" /> No-pressure quotes</span>
              <span className="hidden md:inline text-background/20">·</span>
              <span className="inline-flex items-center gap-1.5"><Award className="h-3.5 w-3.5 text-primary" /> No surprise bills</span>
              <Link to="/reviews" className="ml-2 underline underline-offset-2 hover:text-primary transition-colors">Read reviews →</Link>
            </div>
          </div>
        </div>
      </div>

      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(buildSchema(trust, routing, compliance)) }}
      />
    </section>
  );
}
